import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('shaheen_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

const TYPE_LABELS = {
  monthly_briefing: 'Briefing',
  breach_report:    'Breach Report',
  scan_report:      'Assessment Report',
  incident_report:  'Incident',
  travel_advisory:  'Travel Advisory',
};

const TYPE_COLORS = {
  monthly_briefing: { bg: 'rgba(212,175,55,0.12)', color: 'var(--gold)' },
  breach_report:    { bg: 'rgba(239,68,68,0.12)',   color: 'var(--red)' },
  scan_report:      { bg: 'rgba(59,130,246,0.12)',  color: 'var(--blue)' },
  incident_report:  { bg: 'rgba(245,158,11,0.12)',  color: 'var(--amber)' },
  travel_advisory:  { bg: 'rgba(34,197,94,0.12)',   color: 'var(--green)' },
};

const FILTER_TYPES = [
  { key: '',                 label: 'All' },
  { key: 'monthly_briefing', label: 'Briefings' },
  { key: 'breach_report',    label: 'Breach Reports' },
  { key: 'scan_report',      label: 'Assessment Reports' },
  { key: 'incident_report',  label: 'Incidents' },
  { key: 'travel_advisory',  label: 'Travel' },
];

function fmtDate(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ts.slice(0, 10); }
}

function DocContent({ doc }) {
  if (!doc) return null;
  const c = doc.content || {};

  if (doc.document_type === 'monthly_briefing') {
    return (
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
        {c.briefing || JSON.stringify(c, null, 2)}
      </div>
    );
  }

  if (doc.document_type === 'breach_report') {
    return (
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
          Email: <span style={{ color: 'var(--text-primary)' }}>{c.email}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Breaches found:</div>
        {(c.breach_names || []).map((name, i) => (
          <div key={i} style={{
            fontSize: 12, color: 'var(--red)',
            background: 'rgba(239,68,68,0.07)', borderRadius: 6,
            padding: '6px 10px', marginBottom: 6,
          }}>
            {name}
          </div>
        ))}
        {(c.breaches || []).map((b, i) => b.description && (
          <div key={i} style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--text-secondary)' }}>{b.name}:</strong> {b.description}
          </div>
        ))}
      </div>
    );
  }

  if (doc.document_type === 'scan_report') {
    const risk = c.risk_summary || {};
    const flags = risk.flags || [];
    return (
      <div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>RISK SCORE</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--gold)' }}>{risk.score ?? '—'}</div>
          </div>
          <div style={{ background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>LEVEL</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--gold)' }}>{risk.level ?? '—'}</div>
          </div>
        </div>
        {flags.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>FLAGS</div>
            {flags.map((f, i) => (
              <div key={i} style={{
                fontSize: 12, color: 'var(--text-secondary)',
                padding: '6px 0', borderBottom: '0.5px solid var(--border)',
              }}>
                {f}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  if (doc.document_type === 'incident_report') {
    const sevColor = { CRITICAL: 'var(--red)', HIGH: 'var(--amber)', MEDIUM: 'var(--blue)', LOW: 'var(--green)' };
    return (
      <div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 6,
            background: 'rgba(245,158,11,0.12)', color: 'var(--amber)',
          }}>
            {c.incident_type}
          </span>
          <span style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 6,
            background: 'rgba(239,68,68,0.1)',
            color: sevColor[c.severity] || 'var(--text-primary)',
          }}>
            {c.severity}
          </span>
        </div>
        {c.case_id && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
            Case ID: {c.case_id}
          </div>
        )}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {c.summary}
        </div>
      </div>
    );
  }

  if (doc.document_type === 'travel_advisory') {
    const riskColors = { low: '#22c55e', medium: '#f59e0b', high: '#f97316', critical: '#ef4444' };
    const rc = riskColors[c.risk_level] || '#f59e0b';
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 24 }}>{c.flag || '🌍'}</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>{c.country}</div>
            {c.travel_date && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Travel: {c.travel_date}</div>}
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 8, background: rc + '22', border: `0.5px solid ${rc}`, color: rc }}>
            {(c.risk_level || 'MEDIUM').toUpperCase()} RISK
          </span>
        </div>
        {c.ncsi_score && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            NCSI Score: <strong style={{ color: 'var(--text-primary)' }}>{c.ncsi_score}/100</strong>
            {c.ncsi_rank && ` · Rank #${c.ncsi_rank}`}
          </div>
        )}
        {c.risk_summary && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>{c.risk_summary}</div>
        )}
        {(c.top_threats || []).length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.07em', marginBottom: 6 }}>TOP THREATS</div>
            {c.top_threats.map((t, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '4px 0', display: 'flex', gap: 6 }}>
                <span>{t.icon}</span><strong>{t.name}</strong>
              </div>
            ))}
          </div>
        )}
        {(c.recommendations || []).length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.07em', marginBottom: 6 }}>KEY RECOMMENDATIONS</div>
            {c.recommendations.filter(r => r.priority === 'critical' || r.priority === 'high').slice(0, 5).map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '3px 0', display: 'flex', gap: 6 }}>
                <span style={{ color: riskColors[r.priority] || '#f59e0b' }}>›</span>{r.text}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <pre style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {JSON.stringify(c, null, 2)}
    </pre>
  );
}

export default function Vault() {
  const { client } = useAuth();
  const clientId = client?.id;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [docDetail, setDocDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const fetchDocs = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    const url = filter
      ? `${API}/client/${clientId}/documents?type=${filter}`
      : `${API}/client/${clientId}/documents`;
    authFetch(url)
      .then(r => r.json())
      .then(data => setDocs(data.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [clientId, filter]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openDoc = (doc) => {
    setSelected(doc);
    setDocDetail(null);
    setDetailLoading(true);
    authFetch(`${API}/client/${clientId}/documents/${doc.id}`)
      .then(r => r.json())
      .then(data => setDocDetail(data))
      .catch(() => setDocDetail({ ...doc, content: {} }))
      .finally(() => setDetailLoading(false));
  };

  const closeModal = () => { setSelected(null); setDocDetail(null); };

  const deleteDoc = async (e, docId) => {
    e.stopPropagation();
    if (deletingId) return;
    setDeletingId(docId);
    try {
      await authFetch(`${API}/client/${clientId}/documents/${docId}`, { method: 'DELETE' });
      setDocs(prev => prev.filter(d => d.id !== docId));
      if (selected?.id === docId) closeModal();
    } catch { /* silent — doc stays in list */ }
    finally { setDeletingId(null); }
  };

  const clearAllDocs = async () => {
    setConfirmClear(false);
    const url = filter
      ? `${API}/client/${clientId}/data/documents?type=${filter}`
      : `${API}/client/${clientId}/data/documents`;
    await authFetch(url, { method: 'DELETE' });
    fetchDocs();
  };

  return (
    <div style={{ padding: '20px 16px', overflowY: 'auto', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text-primary)' }}>Document Vault</div>
        {docs.length > 0 && (
          <button
            onClick={() => setConfirmClear(true)}
            style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: '0.5px solid var(--red)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', opacity: 0.8 }}
          >
            Clear {filter ? FILTER_TYPES.find(f => f.key === filter)?.label || 'all' : 'all'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
        Your security reports, briefings, and incident records
      </div>

      {/* Confirm clear dialog */}
      {confirmClear && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '0.5px solid var(--red)', borderRadius: 8, padding: '12px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Delete {filter ? FILTER_TYPES.find(f => f.key === filter)?.label?.toLowerCase() : 'all documents'} from your vault? This cannot be undone.
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => setConfirmClear(false)} style={{ fontSize: 11, padding: '4px 10px', background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-muted)' }}>Go back</button>
            <button onClick={clearAllDocs} style={{ fontSize: 11, padding: '4px 10px', background: 'var(--red)', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#fff', fontWeight: 600 }}>Delete</button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {FILTER_TYPES.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              fontSize: 11, padding: '5px 12px', borderRadius: 16, border: 'none',
              cursor: 'pointer', transition: 'all 0.12s',
              background: filter === f.key ? 'var(--gold)' : 'var(--bg-raised)',
              color: filter === f.key ? 'var(--bg-base)' : 'var(--text-secondary)',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Document list */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>Retrieving your documents...</div>
      ) : docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.2" strokeLinecap="round" style={{ marginBottom: 12 }}>
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
          </svg>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Your document vault is ready</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Your document vault is ready. Reports and briefings will appear here as they are generated.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {docs.map(doc => {
            const typeColor = TYPE_COLORS[doc.document_type] || { bg: 'var(--bg-raised)', color: 'var(--text-muted)' };
            return (
              <div
                key={doc.id}
                onClick={() => openDoc(doc)}
                style={{
                  background: 'var(--bg-surface)', border: '0.5px solid var(--border)',
                  borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'border-color 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  background: typeColor.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={typeColor.color} strokeWidth="1.8" strokeLinecap="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.title}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{
                      fontSize: 10, padding: '2px 7px', borderRadius: 5,
                      background: typeColor.bg, color: typeColor.color,
                    }}>
                      {TYPE_LABELS[doc.document_type] || doc.document_type}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(doc.created_at)}</span>
                  </div>
                </div>
                <button
                  onClick={(e) => deleteDoc(e, doc.id)}
                  disabled={deletingId === doc.id}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted)', opacity: deletingId === doc.id ? 0.4 : 0.6, flexShrink: 0 }}
                  title="Delete this document"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {selected && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            zIndex: 200, padding: '0 0 0 0',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-surface)', borderRadius: '16px 16px 0 0',
              padding: '20px 20px 32px', width: '100%', maxWidth: 600,
              maxHeight: '80vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {selected.title}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {(() => {
                    const tc = TYPE_COLORS[selected.document_type] || { bg: 'var(--bg-raised)', color: 'var(--text-muted)' };
                    return (
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: tc.bg, color: tc.color }}>
                        {TYPE_LABELS[selected.document_type] || selected.document_type}
                      </span>
                    );
                  })()}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(selected.created_at)}</span>
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div style={{ height: '0.5px', background: 'var(--border)', marginBottom: 16 }} />
            {detailLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Retrieving your documents...</div>
            ) : (
              <DocContent doc={docDetail} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
